import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  Typography,
} from "@mui/material";

import { jobService } from "../services/jobService";

// Empty a job's pipeline, keep the job.
//
// Shared by the job card and the job page. It was written inline on the page
// first; the card needed the same thing, and two copies of a confirmation for
// an irreversible action is how the two copies end up saying different things
// about what survives.
//
// WHAT IT HAS TO GET ACROSS
// That this is NOT "delete job". Everything that took work to set up — the
// rounds, the questions, the rubric — is still here afterwards. Somebody
// fixing a bad import needs to know they are not about to rebuild the role.

export default function ClearCandidatesDialog({
  open,
  jobId,
  jobTitle,
  count = 0,
  onClose,
  onDone,
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const run = async () => {
    setBusy(true);
    setError("");
    try {
      const res = await jobService.clearJobCandidates(jobId);
      if (res?.data?.success) onDone?.(res.data.statusMessage);
      else setError(res?.data?.statusMessage || "Could not clear the pipeline.");
    } catch (e) {
      setError(
        e?.response?.data?.statusMessage ||
          "Could not clear the pipeline. Nothing was changed.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
      onClick={(e) => e.stopPropagation()}
    >
      <DialogContent sx={{ p: 3 }}>
        <Typography sx={{ fontSize: 17, fontWeight: 700, color: "#18181b", lineHeight: 1.35 }}>
          Remove {count > 0 ? `all ${count} candidate${count === 1 ? "" : "s"}` : "all candidates"}
          {jobTitle ? ` from “${jobTitle}”` : ""}?
        </Typography>

        <Typography sx={{ fontSize: 13.5, color: "#71717a", mt: 1, lineHeight: 1.6 }}>
          Their resumes, scores and interviews go with them. This cannot be undone.
        </Typography>

        <Typography sx={{ fontSize: 12.5, color: "#a1a1aa", mt: 1.25, lineHeight: 1.55 }}>
          The job stays exactly as it is — rounds, questions and rubric are
          untouched, so you can start collecting again straight away. Anyone who
          has also applied to another job keeps their record.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mt: 2, borderRadius: 1.5 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", mt: 2.5 }}>
          <Button
            onClick={onClose}
            disabled={busy}
            sx={{ textTransform: "none", fontWeight: 650, color: "#71717a" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disableElevation
            disabled={busy}
            onClick={run}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              bgcolor: "#b45309",
              "&:hover": { bgcolor: "#92400e" },
            }}
          >
            {busy ? "Removing…" : "Remove all"}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
